import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import * as trainersApi from '../../api/trainers'
import { useUIStore } from '../../stores/uiStore'
import Card from '../../components/common/Card'
import Button from '../../components/common/Button'
import Input from '../../components/common/Input'
import Spinner from '../../components/common/Spinner'

/**
 * Trainer Edit Page
 * Form to edit an existing trainer profile
 */
export default function TrainerEdit() {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { showToast } = useUIStore()
  
  const [formData, setFormData] = useState({
    uid: '',
    first_name: '',
    last_name: '',
    email: '',
    iban: '',
    trainer_tennaamstelling: '',
  })

  // Fetch trainer data
  const { data: trainer, isLoading } = useQuery({
    queryKey: ['trainer', id],
    queryFn: () => trainersApi.getTrainer(id),
  })

  // Populate form when trainer data loads
  useEffect(() => {
    if (trainer) {
      setFormData({
        uid: trainer.uid || '',
        first_name: trainer.first_name || '',
        last_name: trainer.last_name || '',
        email: trainer.email || '',
        iban: trainer.iban || '',
        trainer_tennaamstelling: trainer.trainer_tennaamstelling || '',
      })
    }
  }, [trainer])

  const updateMutation = useMutation({
    mutationFn: (data) => trainersApi.updateTrainer(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['trainer', id])
      queryClient.invalidateQueries(['trainers'])
      queryClient.invalidateQueries(['dashboard-stats'])
      showToast('Trainer updated successfully!', 'success')
      navigate(`/trainers/${id}`)
    },
    onError: (error) => {
      showToast(error.message || 'Failed to update trainer', 'error')
    },
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    updateMutation.mutate(formData)
  }

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="lg" />
      </div>
    )
  }

  if (!trainer) {
    return (
      <div className="max-w-2xl mx-auto text-center py-12">
        <p className="text-gray-500">Trainer not found</p>
        <Button 
          variant="primary" 
          onClick={() => navigate('/trainers')}
          className="mt-4"
        >
          Back to Trainers
        </Button>
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Edit Trainer</h1>
        <p className="mt-2 text-gray-600">Update the trainer's information</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <Card.Header>
            <h2 className="text-lg font-semibold text-gray-900">Personal Information</h2>
          </Card.Header>
          <Card.Body>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="First Name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                  placeholder="John"
                />

                <Input
                  label="Last Name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                  placeholder="Doe"
                />
              </div>

              <Input
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="john.doe@example.com"
              />

              <Input
                label="IBAN (Optional)"
                name="iban"
                value={formData.iban}
                onChange={handleChange}
                placeholder="NL91ABNA0417164300"
              />

              <Input
                label="Tennaamstelling (Optional)"
                name="trainer_tennaamstelling"
                value={formData.trainer_tennaamstelling}
                onChange={handleChange}
                placeholder="e.g., T.n.v. John Doe"
                helpText="Name to which payments should be addressed"
              />

              <Input
                label="UID (Optional)"
                name="uid"
                value={formData.uid}
                onChange={handleChange}
                placeholder="Unique identifier"
              />
            </div>
          </Card.Body>
        </Card>

        {/* Form Actions */}
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate(`/trainers/${id}`)}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={updateMutation.isPending}
          >
            Save Changes
          </Button>
        </div>
      </form>
    </div>
  )
}